/**
 * Hook for threaded comments with real-time WebSocket updates.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { CommentsApi, type Comment } from '@/api/generated';
import { apiConfig, axiosInstance } from '@/api/client';
import { useWebSocket } from './useWebSocket';

export interface CommentNode extends Comment {
  depth: number;
  childNodes: CommentNode[];
}

export interface UseCommentsReturn {
  comments: CommentNode[];
  isLoading: boolean;
  isConnected: boolean;
  postComment: (body: string, parentId?: string) => Promise<void>;
  editComment: (id: string, body: string) => Promise<void>;
  deleteComment: (id: string) => Promise<void>;
  toggleUpvote: (id: string, hasUpvoted: boolean) => Promise<void>;
}

const api = new CommentsApi(apiConfig, '', axiosInstance);

/**
 * Build a depth-annotated tree from a flat list.
 *
 * Relies on the backend returning comments sorted by created_at ascending so
 * that every parent is guaranteed to appear in the array before its children.
 * This ensures parentNode.depth is already set when we process a child.
 */
function buildTree(flat: Comment[]): CommentNode[] {
  const map = new Map<string, CommentNode>();
  const roots: CommentNode[] = [];

  for (const c of flat) {
    map.set(c.id, { ...c, depth: 0, childNodes: [] });
  }

  for (const node of map.values()) {
    if (node.parent && map.has(node.parent)) {
      const parentNode = map.get(node.parent)!;
      node.depth = parentNode.depth + 1;
      parentNode.childNodes.push(node);
    } else {
      roots.push(node);
    }
  }

  return roots;
}

export function useComments(threadId: string): UseCommentsReturn {
  const [flat, setFlat] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const upsert = (updated: Comment) =>
    setFlat((prev) => {
      const idx = prev.findIndex((c) => c.id === updated.id);
      if (idx === -1) return [...prev, updated];
      const next = [...prev];
      next[idx] = updated;
      return next;
    });

  const { isConnected } = useWebSocket(`/ws/comments/${threadId}/`, {
    onMessage: (msg) => {
      const message = msg as { type: string; data?: Comment | Comment[] };
      switch (message.type) {
        case 'initial_state':
          setFlat(message.data as Comment[]);
          setIsLoading(false);
          break;

        case 'comment.created':
        case 'comment.updated':
        case 'comment.deleted': {
          const incoming = message.data as Comment;
          // The consumer skips the per-client has_upvoted DB check for non-upvote
          // events to avoid N×clients queries per mutation. The broadcast value
          // reflects the requesting user's state, so we preserve the client's
          // own has_upvoted from initial_state or the last comment.upvote event.
          setFlat((prev) => {
            const idx = prev.findIndex((c) => c.id === incoming.id);
            const merged = { ...incoming, has_upvoted: idx !== -1 ? prev[idx].has_upvoted : false };
            if (idx === -1) return [...prev, merged];
            const next = [...prev];
            next[idx] = merged;
            return next;
          });
          break;
        }

        case 'comment.upvote':
          // has_upvoted is authoritative here — consumer re-checked for this client.
          upsert(message.data as Comment);
          break;
      }
    },
  });

  const fetchComments = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await api.commentsList({ params: { thread_id: threadId } });
      setFlat(res.data as Comment[]);
    } catch (err) {
      console.error('Failed to fetch comments:', err);
    } finally {
      setIsLoading(false);
    }
  }, [threadId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const postComment = useCallback(
    async (body: string, parentId?: string) => {
      await api.commentsCreate({ thread_id: threadId, body, parent: parentId ?? null });
    },
    [threadId]
  );

  const editComment = useCallback(async (id: string, body: string) => {
    await api.commentsPartialUpdate(id, { body });
  }, []);

  const deleteComment = useCallback(async (id: string) => {
    await api.commentsDestroy(id);
  }, []);

  const toggleUpvote = useCallback(async (id: string, hasUpvoted: boolean) => {
    if (hasUpvoted) {
      await api.commentsUpvoteDestroy(id);
    } else {
      await api.commentsUpvoteCreate(id);
    }
  }, []);

  const comments = useMemo(() => buildTree(flat), [flat]);

  return { comments, isLoading, isConnected, postComment, editComment, deleteComment, toggleUpvote };
}
